import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { ApiError } from '../../../lib/api-error';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function CustomerProfilePage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const profile = useQuery({
    queryKey: ['customer', 'profile'],
    queryFn: () => customersApi.getProfile(),
    retry: false,
  });

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [occupation, setOccupation] = useState('');

  useEffect(() => {
    if (profile.data !== undefined) {
      setFirstName(profile.data.firstName);
      setMiddleName(profile.data.middleName ?? '');
      setLastName(profile.data.lastName);
      setPhoneNumber(profile.data.phoneNumber);
      setOccupation(profile.data.occupation ?? '');
    }
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        firstName,
        middleName: middleName === '' ? undefined : middleName,
        lastName,
        phoneNumber,
        occupation: occupation === '' ? undefined : occupation,
      };

      const missingProfile =
        profile.isError && profile.error instanceof ApiError && profile.error.status === 404;

      return missingProfile
        ? customersApi.createProfile(payload)
        : customersApi.updateProfile(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer', 'profile'] });
    },
  });

  const missingProfile =
    profile.isError && profile.error instanceof ApiError && profile.error.status === 404;
  const isNew = missingProfile;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Customer profile</h1>
        <p className={styles.pageSubtitle}>
          Your contact details for contract and repayment correspondence.
        </p>
      </header>

      {profile.isPending && <PageSpinner label="Loading profile" />}

      <section className={styles.card} aria-labelledby="account-overview-heading">
        <h2 className={styles.cardTitle} id="account-overview-heading">
          Account and records
        </h2>
        <dl className={styles.definitionList}>
          <dt>Verified account email</dt>
          <dd>{user?.email ?? 'Unavailable'}</dd>
        </dl>
        <p className={styles.pageSubtitle}>
          Updating your current profile or addresses does not alter contracts you already accepted
          or the historical identity shown on their payment receipts.
        </p>
        <nav className={styles.actions} aria-label="Customer account records">
          <Link to={paths.customerAddresses}>Addresses</Link>
          <Link to={paths.customerBankAccounts}>Bank accounts</Link>
          <Link to={paths.customerConsents}>Consents</Link>
          <Link to={paths.sessions}>Sessions and security</Link>
        </nav>
      </section>

      {(profile.isSuccess || profile.isError) && (
        <section className={styles.card} aria-labelledby="profile-form-heading">
          <h2 className={styles.cardTitle} id="profile-form-heading">
            {isNew ? 'Create profile' : 'Update profile'}
          </h2>

          {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}
          {save.isSuccess && <Alert tone="success">Profile saved.</Alert>}

          <form
            className={styles.filters}
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <FormField
              label="First name"
              name="firstName"
              value={firstName}
              required
              onChange={(event) => {
                setFirstName(event.target.value);
              }}
            />
            <FormField
              label="Middle name"
              name="middleName"
              value={middleName}
              onChange={(event) => {
                setMiddleName(event.target.value);
              }}
            />
            <FormField
              label="Last name"
              name="lastName"
              value={lastName}
              required
              onChange={(event) => {
                setLastName(event.target.value);
              }}
            />
            <FormField
              label="Phone number"
              name="phoneNumber"
              value={phoneNumber}
              required
              onChange={(event) => {
                setPhoneNumber(event.target.value);
              }}
            />
            <FormField
              label="Occupation"
              name="occupation"
              value={occupation}
              onChange={(event) => {
                setOccupation(event.target.value);
              }}
            />
            <Button type="submit" disabled={save.isPending}>
              {isNew ? 'Create profile' : 'Save changes'}
            </Button>
          </form>

          {profile.isSuccess && (
            <dl className={styles.definitionList}>
              <dt>Customer number</dt>
              <dd>{profile.data.customerNumber}</dd>
              <dt>Onboarding stage</dt>
              <dd>{profile.data.onboardingStage.replace(/_/g, ' ').toLowerCase()}</dd>
            </dl>
          )}
        </section>
      )}
    </div>
  );
}
